/**
 * `useMeasure` is a helper function that will provide various measurements and
 * coordinates for a given DOM element.
 */
import * as React from "react"
import useResize from "../useResize"
import useDebouncedCallback from "../useDebouncedCallback"
import { stringLiteralArray } from "@oui/system/utils"
import { NonEmptyArray, Unpack } from "@oui/system/typelevel"

type KeyMap = { [key: string]: string }
type Entries = [string, string]
type Measure = { [key: string]: number }
type Rect = ClientRect | DOMRect | Measure
type Style = CSSStyleDeclaration | Measure
type Offset = Pick<
  HTMLElement,
  "offsetHeight" | "offsetWidth" | "offsetLeft" | "offsetTop"
>
type Scroll = Pick<
  HTMLElement,
  "scrollHeight" | "scrollWidth" | "scrollLeft" | "scrollTop"
>
export type MeasureType = typeof measurements[number]
type Position = typeof defaultPosition
type Dimension = typeof defaultDimension
type CoordsAndDimensions = typeof defaultCoordsAndDimensions
export type Client = Position & Dimension
type Margin = Position
type Measurements = {
  client: Client
  margin: Margin
  scroll: CoordsAndDimensions
  offset: CoordsAndDimensions
}
type ExtractTypes<T> = Extract<MeasureType, T>
type PickTypes<T> = Pick<Measurements, ExtractTypes<T>>
type MeasureSet<T> = Unpack<PickTypes<T>>

const measurements = stringLiteralArray([
  "client",
  "margin",
  "scroll",
  "offset"
])

const defaultDimension = {
  width: 0,
  height: 0
}

const defaultCoords = {
  top: 0,
  left: 0
}

const defaultPosition = {
  ...defaultCoords,
  right: 0,
  bottom: 0
}

const defaultClient = {
  ...defaultPosition,
  ...defaultDimension
}

const defaultCoordsAndDimensions = {
  ...defaultCoords,
  ...defaultDimension
}

const getWithDefault = (
  fn: (el: Element) => Client | Position | CoordsAndDimensions,
  defaults: Client | Position | CoordsAndDimensions,
  el: Element | null
) => {
  return el === null || el == undefined ? { ...defaults } : fn(el)
}

// TODO: remove casting and align types
const getClient = (el: Element) => {
  const rect: Rect =
    el && el.getBoundingClientRect ? el.getBoundingClientRect() : {}
  const keys = ["top", "bottom", "left", "right", "width", "height"]
  const reducer = (acc: Measure, key: string) => {
    acc[key] = (rect as Measure)[key] ?? (defaultClient as Measure)[key]
    return acc
  }
  return keys.reduce(reducer, { ...defaultClient }) as Client
}

const getMargin = (el: Element) => {
  const style: Style = window.getComputedStyle
    ? window.getComputedStyle(el)
    : {}

  const keyMap: KeyMap = {
    top: "marginTop",
    right: "marginRight",
    bottom: "marginBottom",
    left: "marginLeft"
  }

  const reducer = (acc: Measure, [key, val]: Entries) => {
    const attr = (style as Measure)[val]
    acc[key] = attr
      ? parseInt(String(attr), 10)
      : (defaultPosition as Measure)[key]
    return acc
  }
  return Object.entries(keyMap).reduce(reducer, {
    ...defaultPosition
  }) as Margin
}

const getScroll = (el: Scroll): CoordsAndDimensions => {
  const keyMap: KeyMap = {
    height: "scrollHeight",
    width: "scrollWidth",
    left: "scrollLeft",
    top: "scrollTop"
  }

  return getFromElement(el, keyMap)
}

const getOffset = (el: Element): CoordsAndDimensions => {
  const keyMap: KeyMap = {
    height: "offsetHeight",
    width: "offsetWidth",
    left: "offsetLeft",
    top: "offsetTop"
  }

  return getFromElement(el, keyMap)
}

const getFromElement = (el: Scroll | Offset, keyMap: KeyMap) => {
  const reducer = (acc: Measure, [key, val]: Entries) => {
    acc[key] =
      (el as Measure)[val] ?? (defaultCoordsAndDimensions as Measure)[key]
    return acc
  }

  return Object.entries(keyMap).reduce(reducer, {
    ...defaultCoordsAndDimensions
  }) as CoordsAndDimensions
}

const reducer = (el: Element | null) => (
  acc: Measurements,
  measureType: MeasureType
) => {
  if (el !== null && el !== undefined) {
    switch (measureType) {
      case "client":
        acc.client = getWithDefault(getClient, defaultClient, el) as Client
        break
      case "margin":
        acc.margin = getWithDefault(getMargin, defaultPosition, el) as Margin
        break
      case "scroll":
        acc.scroll = getWithDefault(
          getScroll,
          defaultCoordsAndDimensions,
          el
        ) as CoordsAndDimensions
        break
      case "offset":
        acc.offset = getWithDefault(
          getOffset,
          defaultCoordsAndDimensions,
          el
        ) as CoordsAndDimensions
        break
    }
  }

  return acc
}

/**
 * @param element DOM element to track for resize and scroll events
 * @param measureTypes Specify all measurements or MeasureSet including BoundingClientRect, Offset, Margin and Scroll
 */
const getMeasurements = <P extends MeasureType>(
  element: HTMLElement | null,
  measureTypes: NonEmptyArray<P>
) => {
  return measureTypes.reduce(reducer(element), {} as any) as MeasureSet<P>
}

/**
 * @param element DOM element to track for resize and scroll events
 * @param measureTypes Specify all measurements or MeasureSet including BoundingClientRect, Offset, Margin and Scroll
 * @param delay Debounce delay (milliseconds) for triggering onChange handler @default 100
 */
const useMeasure = <P extends MeasureType>(
  element: HTMLElement | null,
  measureTypes: NonEmptyArray<P>,
  delay = 100
) => {
  const [measure, setMeasure] = React.useState(
    getMeasurements(element, measureTypes)
  )
  const onChange = React.useCallback(() => {
    setMeasure(getMeasurements(element, measureTypes))
  }, [element, measureTypes])
  const debouncedCallback = useDebouncedCallback(onChange, delay)
  const changeHandler = delay > 0 ? debouncedCallback : onChange

  useResize(element, changeHandler, delay)
  useResize(document.body, changeHandler, delay)

  React.useEffect(() => {
    window.addEventListener("scroll", changeHandler)
    return () => {
      window.removeEventListener("scroll", changeHandler)
    }
  }, [changeHandler, onChange])

  return measure as MeasureSet<P>
}

export { measurements, getMeasurements, getClient, getMargin, getOffset }
export default useMeasure
